import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Banknote } from "lucide-react";
import { formatUSD } from "@/lib/marketplaceTerms";

const METHODS = [["cash", "Cash"], ["check", "Check"], ["bank_transfer", "Bank transfer"], ["other", "Other"]];

export const OfflinePaymentDialog = ({ bookingId, remaining, onRecorded }) => {
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState(String(remaining || ""));
  const [method, setMethod] = useState("cash");
  const [reference, setReference] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (reason.trim().length < 5) return toast.error("A reason (at least 5 characters) is required — it goes to the audit log.");
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/bookings/${bookingId}/record-offline-payment`, {
        amount: Number(amount), method, reference, reason,
      });
      toast.success(`Offline payment recorded · ${formatUSD(data.remaining)} remaining`);
      setOpen(false);
      onRecorded?.(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not record payment");
    } finally { setBusy(false); }
  };

  return (
    <>
      <Button data-testid="record-offline-payment-btn" variant="outline" onClick={() => { setAmount(String(remaining)); setOpen(true); }}
              className="rounded-full w-full h-11 mt-4">
        <Banknote className="w-4 h-4 mr-2" /> Record offline payment
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="offline-payment-dialog" className="max-w-md bg-[#FDFBF7]">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Record offline payment</DialogTitle></DialogHeader>
          <div className="space-y-4 text-sm">
            <p className="text-xs text-[color:var(--text-muted)]">
              Money the planner paid the vendor directly (cash, check, bank). It reduces the balance owed on this booking
              but is never held, released or refunded by NContro. Remaining: <strong>{formatUSD(remaining)}</strong>.
            </p>
            <div>
              <label className="eyebrow block mb-2" htmlFor="offline-amount">Amount</label>
              <Input id="offline-amount" data-testid="offline-amount-input" type="number" step="0.01" min="0.01" max={remaining}
                     value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-white h-11" />
            </div>
            <div>
              <label className="eyebrow block mb-2">Method</label>
              <div className="grid grid-cols-4 gap-2">
                {METHODS.map(([k, label]) => (
                  <button key={k} type="button" data-testid={`offline-method-${k}`} onClick={() => setMethod(k)}
                          className={`p-2 rounded-md border text-xs ${method === k ? "border-[color:var(--accent)] bg-white" : "border-[color:var(--border)]"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="offline-reference">Reference (optional)</label>
              <Input id="offline-reference" data-testid="offline-reference-input" value={reference} onChange={(e) => setReference(e.target.value)}
                     placeholder="Check number, transfer ID…" className="bg-white h-11" />
            </div>
            <div>
              <label className="eyebrow block mb-2" htmlFor="offline-reason">Reason (required, audited)</label>
              <Textarea id="offline-reason" data-testid="offline-reason-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
                        placeholder="Why this payment is being recorded outside Stripe" className="bg-white" />
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy} className="rounded-full">Cancel</Button>
            <Button data-testid="offline-payment-submit" onClick={submit} disabled={busy} className="rounded-full bg-[#1A1A1A] hover:bg-black text-white">
              {busy ? "Recording…" : "Record payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
